import { useEffect, useMemo } from 'react';

import { WatchQueryFetchPolicy } from 'apollo-client';

import { LoyaltyServiceMode, useLoyaltyUserRewardsQuery } from 'generated/graphql-gateway';
import { useLoyaltyRewardsQuery } from 'generated/sanity-graphql';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { LoyaltyEngineReward, LoyaltyReward } from 'state/loyalty/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';

export type IEngineRewardsMap = {
  [rewardId: string]: LoyaltyEngineReward;
};
interface IParsedEngineRewards {
  engineRewardIds: string[];
  engineRewardsMap: IEngineRewardsMap;
}
export type ISanityRewardsMap = Record<LoyaltyReward['_id'], LoyaltyReward>;

interface IUseLoyaltyRewardsList {
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
}

export const useLoyaltyRewardsList = ({
  fetchPolicy = 'cache-first',
  skip = false,
}: IUseLoyaltyRewardsList = {}) => {
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();

  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser } = useLoyaltyUser();
  const dispatch = useAppDispatch();
  const shouldRefetch = useAppSelector(selectors.loyalty.selectShouldRefetchRewards);

  // To Do: move this logic to loyalty GQL server in order to make 1 call on the frontend
  // and to avoid creating rewards map
  const {
    data: engineResponse,
    loading: engineRewardsLoading,
    refetch: refetchRewards,
  } = useLoyaltyUserRewardsQuery({
    skip: !loyaltyEnabled || !loyaltyUser?.id || skip,
    variables: {
      loyaltyId: loyaltyUser?.id || '',
      where: {
        ignorePointBalance: true,
        serviceMode: LoyaltyServiceMode[serviceMode || ''] || undefined,
        storeId: store?.number,
      },
    },
    fetchPolicy,
  });

  const { engineRewardIds, engineRewardsMap } = useMemo(
    () =>
      (engineResponse?.loyaltyUserV2?.rewards || []).reduce(
        (acc: IParsedEngineRewards, reward) => {
          // Extract engineIds to query for rewards in sanity
          if (reward?.id) {
            acc.engineRewardIds.push(reward.id);
            acc.engineRewardsMap[reward.id] = reward;
          }
          return acc;
        },
        { engineRewardIds: [], engineRewardsMap: {} }
      ),
    [engineResponse]
  );

  const { data, loading: sanityRewardsLoading } = useLoyaltyRewardsQuery({
    skip: !engineRewardIds?.length || skip,
  });

  const rewardsByEngineId = useMemo(
    () => data?.allRewards.filter(reward => engineRewardsMap[reward?.loyaltyEngineId || '']),
    [data, engineRewardsMap]
  );

  const sanityRewardsMap = useMemo(() => {
    if (!rewardsByEngineId) {
      return null;
    }
    return rewardsByEngineId.reduce((acc: ISanityRewardsMap, reward) => {
      acc[reward._id] = reward;
      return acc;
    }, {});
  }, [rewardsByEngineId]);

  useEffect(() => {
    if (shouldRefetch) {
      refetchRewards();
      dispatch(actions.loyalty.setShouldRefetchRewards(false));
    }
  }, [dispatch, refetchRewards, shouldRefetch]);

  useEffect(() => {
    if (engineResponse?.loyaltyUserV2?.rewards?.length) {
      dispatch(
        actions.loyalty.createAvailableLoyaltyRewardsMap(engineResponse.loyaltyUserV2.rewards)
      );
    }
  }, [dispatch, engineResponse]);

  const rewardsLoading = engineRewardsLoading || sanityRewardsLoading;

  return {
    rewards: rewardsByEngineId || null,
    loading: rewardsLoading,
    engineRewardsMap,
    sanityRewardsMap,
    refetchRewards,
  };
};
